import {
  LoyaltyOfferValidity,
  LoyaltyRewardDistribution,
  LoyaltyTargetConfig,
} from "../loyaltyTypes";

/**
 * What a BOGO and a bundle actually are, read from the offer's own shape.
 *
 * Not `offerCategory`: that is the service's presentation label and is
 * narrower than the shape it names — it only calls a bundle a BUNDLE when the
 * value type is NEW_VALUE, so "any 2 for 20 off" arrives as ITEM_DISCOUNT
 * while the same service's UI strategy gives it bundle copy.
 *
 * Kept free of `LoyaltyOffer` so the offer class and the plain redux payloads
 * can share one definition.
 */

/** A buy side to satisfy before the reward is given. */
export function hasBuyRequirements(offer: {
  validity?: Pick<LoyaltyOfferValidity, "requirements">;
}): boolean {
  return Boolean(offer.validity?.requirements);
}

/** A fixed number of items, above one, sharing one pooled reward. */
export function isBundleShaped(config?: LoyaltyTargetConfig): boolean {
  const maximum = config?.maximumItemCount;

  if (config == null || maximum == null) {
    return false;
  }

  return (
    maximum === config.minimumItemCount &&
    config.minimumItemCount > 1 &&
    config.rewardDistribution === LoyaltyRewardDistribution.AGGREGATE
  );
}

/**
 * A bundle with nothing to choose: the forced quantities already fill it, so
 * the items are fixed and can be named and priced up front.
 *
 * Mirrors `BundleStrategy.isFixedSelection` in the loyalty service, which uses
 * the same two shapes to decide whether its copy can list the items. The two
 * have to agree — the service's wording promises what this screen shows.
 */
export function isFixedBundle(config?: LoyaltyTargetConfig): boolean {
  if (!isBundleShaped(config)) {
    return false;
  }

  const entities = config?.targetEntities ?? [];
  const count = config?.maximumItemCount ?? 0;
  if (!entities.length) {
    return false;
  }

  const forced = entities.reduce(
    (sum, entity) => sum + Math.max(entity.minimumQuantity ?? 1, 1),
    0
  );

  return entities.length === count || forced === count;
}
